import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { consultingServices, marketingSolutions, pageMeta } from "@/data/mockData";

function ServiceGrid({ items, idPrefix }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {items.map((s, i) => (
        <motion.article
          key={s.title}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.45, delay: i * 0.06 }}
          data-testid={`${idPrefix}-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className="service-card group bg-white border border-gray-100 rounded-2xl p-7"
        >
          {s.iconPng ? (
            <picture>
              <img
                src={s.iconPng}
                alt={s.alt || s.title}
                loading="lazy"
                decoding="async"
                draggable="false"
                className="service-icon h-20 sm:h-24 max-w-full object-contain object-left mb-5 select-none will-change-transform transition-transform duration-150 ease-out"
              />
            </picture>
          ) : (
            <div className="h-12 w-12 rounded-full bg-[#fffbea] inline-flex items-center justify-center mb-5 group-hover:bg-[#ffde59] transition-colors">
              <i className={`${s.icon} text-[#023e8a] text-lg`} aria-hidden="true" />
            </div>
          )}
          <h3 className="font-serif text-xl text-[#0a2540] mb-2.5 leading-tight">{s.title}</h3>
          <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
        </motion.article>
      ))}
    </div>
  );
}

export default function Services() {
  return (
    <>
      <SEO {...pageMeta.services} />

      {/* Hero */}
      <section className="bg-white pt-20 pb-12 md:py-24" data-testid="services-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-4">
              Services
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0a2540] leading-[1.05]">
              A full-spectrum advisory service.
            </h1>
            <p className="mt-7 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              From consulting frameworks that shape every decision, to marketing solutions that ensure your property reaches the right buyer — Johnny offers an integrated approach to Singapore property advisory.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Consulting */}
      <section className="bg-[#fafafa] py-20 md:py-28" data-testid="consulting-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl mb-12">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-3">
              Consulting Services
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#0a2540] leading-[1.1]">
              Strategic advisory that compounds over time.
            </h2>
          </Reveal>
          <ServiceGrid items={consultingServices} idPrefix="consulting" />
        </div>
      </section>

      {/* Marketing */}
      <section className="bg-white py-20 md:py-28" data-testid="marketing-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl mb-12">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-3">
              Marketing Solutions
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#0a2540] leading-[1.1]">
              Selling a property is a marketing exercise.
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Modern buyers research across multiple channels before they ever step into a viewing. Johnny's marketing stack ensures your listing is visible, compelling, and competitive everywhere it matters.
            </p>
          </Reveal>
          <ServiceGrid items={marketingSolutions} idPrefix="marketing" />
        </div>
      </section>

      <CTABanner />
    </>
  );
}
